
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { scrollToTop, saveScrollPosition } from "@/utils/scrollManager";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * A hook that scrolls the window to the top when the route changes
 * with enhanced mobile support
 * @param options Configuration options
 */
export const useScrollToTop = (options: {
  behavior?: ScrollBehavior;
  delay?: number;
  savePositions?: boolean;
} = {}) => {
  const { pathname } = useLocation();
  const { behavior = "auto", delay = 0, savePositions = true } = options;
  const isMobile = useIsMobile();

  useEffect(() => {
    // Save current position before navigating if enabled
    if (savePositions) {
      saveScrollPosition(window.location.pathname);
    }

    // Use different timing for mobile vs desktop
    const scrollDelay = isMobile ? 50 : delay;
    
    // Use auto scroll behavior on mobile for more responsiveness
    const scrollBehavior = isMobile ? 'auto' : behavior;
    
    scrollToTop({
      behavior: scrollBehavior, 
      delay: scrollDelay
    });
    
    // On mobile, we need to re-trigger to ensure proper scroll position
    if (isMobile) {
      const secondaryTimer = setTimeout(() => {
        scrollToTop({ behavior: 'auto', delay: 0 });
      }, 300);
      
      return () => clearTimeout(secondaryTimer);
    }
  }, [pathname, behavior, delay, savePositions, isMobile]);
};
